/**
 * Sidebar Component - Enhanced Session List
 */
import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Fade,
  Avatar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatIcon from '@mui/icons-material/Chat';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkIcon from '@mui/icons-material/Work';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PublicIcon from '@mui/icons-material/Public';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS, ja } from 'date-fns/locale';

import useChatStore from '../store/chatStore';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';
import AutoIngestToggle from './AutoIngestToggle';

export default function Sidebar({ onClose }) {
  const { user } = useAuthStore();
  const {
    sessions,
    currentSession,
    loadingSessions,
    createSession,
    selectSession,
    deleteSession,
    clearMessages,
  } = useChatStore();

  const { t, i18n } = useTranslation(['common', 'chat']);

  const currentLocale = (() => {
    const lang = (i18n.resolvedLanguage || i18n.language || 'vi').split('-')[0];
    if (lang === 'en') return enUS;
    if (lang === 'ja') return ja;
    return vi;
  })();

  const handleNewChat = async () => {
    await createSession(t('chat:session.new'));
    if (onClose) onClose();
  };

  const handleSelectSession = async (sessionId) => {
    await selectSession(sessionId);
    if (onClose) onClose();
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (window.confirm(t('chat:actions.confirmDeleteConversation'))) {
      await deleteSession(sessionId);
    }
  };

  // Get department styles for all roles
  const getDepartmentStyles = () => {
    if (user?.department === 'ALL' || user?.role === 'ADMIN' || user?.role === 'MASTER') {
      return {
        color: '#7c4dff',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        icon: <AdminPanelSettingsIcon />,
        bgLight: '#f3e5f5',
      };
    }
    if (user?.department === 'HR') {
      return {
        color: '#1976d2',
        gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        icon: <WorkIcon />,
        bgLight: '#e3f2fd',
      };
    }
    if (user?.department === 'SALES') {
      return {
        color: '#2e7d32',
        gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
        icon: <StorefrontIcon />,
        bgLight: '#e8f5e9',
      };
    }
    if (user?.department === 'GUEST' || user?.role === 'GUEST') {
      return {
        color: '#f57c00',
        gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        icon: <PublicIcon />,
        bgLight: '#fff3e0',
      };
    }
    return {
      color: '#757575',
      gradient: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
      icon: <ChatIcon />,
      bgLight: '#f5f5f5',
    };
  };

  const { color: departmentColor, gradient: departmentGradient, icon: departmentIcon, bgLight } = getDepartmentStyles();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(to bottom, #fafafa, #f5f5f5)',
      }}
    >
      {/* Header with gradient and glassmorphism */}
      <Box
        sx={{
          p: 3,
          background: departmentGradient,
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, position: 'relative' }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.25)',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '& svg': {
                fontSize: 28,
              },
            }}
          >
            {departmentIcon}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {t('common:brand.chat')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
              {user?.full_name}
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={<ChatBubbleIcon sx={{ fontSize: 14 }} />}
          label={t('chat:session.count', { count: sessions.length })}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.25)',
            color: 'white',
            fontWeight: 600,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            '& .MuiChip-icon': {
              color: 'white',
            },
          }}
        />
      </Box>

      {/* New Chat Button with modern design */}
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 22 }} />}
          onClick={handleNewChat}
          sx={{
            background: departmentGradient,
            py: 1.5,
            px: 3,
            borderRadius: 3,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: `0 6px 20px ${departmentColor}50`,
            border: '2px solid rgba(255,255,255,0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover': {
              transform: 'translateY(-2px) scale(1.02)',
              boxShadow: `0 8px 25px ${departmentColor}70`,
              '&::before': {
                left: '100%',
              },
            },
            '&:active': {
              transform: 'translateY(0) scale(0.98)',
            },
          }}
        >
          {t('chat:actions.newChat')}
        </Button>
      </Box>

      {/* Auto-Ingest Toggle */}
      <AutoIngestToggle />

      <Divider sx={{ mx: 2 }} />

      {/* Session List with better styling */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#bdbdbd',
            borderRadius: '10px',
            '&:hover': {
              background: '#9e9e9e',
            },
          },
        }}
      >
        {loadingSessions ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 2 }}>
            <CircularProgress size={40} sx={{ color: departmentColor }} />
            <Typography variant="body2" color="text.secondary">
              {t('common:status.loading')}
            </Typography>
          </Box>
        ) : sessions.length === 0 ? (
          <Fade in>
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('chat:status.noConversation')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('chat:actions.startNow')}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <List sx={{ p: 1 }}>
            {sessions.map((session, index) => (
              <Fade in key={session.id} timeout={300 + index * 50}>
                <ListItem
                  disablePadding
                  sx={{ mb: 0.5 }}
                >
                  <ListItemButton
                    selected={currentSession?.id === session.id}
                    onClick={() => handleSelectSession(session.id)}
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&.Mui-selected': {
                        background: departmentGradient,
                        color: 'white',
                        boxShadow: `0 4px 12px ${departmentColor}50`,
                        transform: 'scale(1.02)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: 'white',
                          borderRadius: '0 4px 4px 0',
                        },
                        '&:hover': {
                          background: departmentGradient,
                          transform: 'scale(1.02) translateX(2px)',
                        },
                      },
                      '&:hover': {
                        bgcolor: bgLight,
                        transform: 'translateX(4px)',
                        boxShadow: `0 2px 8px ${departmentColor}20`,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        mr: 1.5,
                        bgcolor: currentSession?.id === session.id
                          ? 'rgba(255,255,255,0.2)'
                          : bgLight,
                        color: currentSession?.id === session.id ? 'white' : departmentColor,
                        fontSize: '1rem',
                      }}
                    >
                      <ChatBubbleIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <ListItemText
                      primary={session.title || t('chat:session.new')}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: currentSession?.id === session.id
                                ? 'rgba(255,255,255,0.8)'
                                : 'text.secondary',
                            }}
                          >
                            {session.updated_at
                              ? formatDistanceToNow(new Date(session.updated_at), {
                                addSuffix: true,
                                locale: currentLocale,
                              })
                              : ''}
                          </Typography>
                        </Box>
                      }
                      primaryTypographyProps={{
                        noWrap: true,
                        fontSize: '0.9rem',
                        fontWeight: currentSession?.id === session.id ? 600 : 400,
                      }}
                    />
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleDeleteSession(e, session.id)}
                      sx={{
                        opacity: 0.6,
                        color: currentSession?.id === session.id ? 'white' : 'text.secondary',
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 1,
                          color: 'error.main',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              </Fade>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}
